import { ConnectorHosting } from "@p0security/integrations-app-shared/components";
import {
  newVaultElement,
  withoutAzureKeyVault,
} from "@p0security/integrations-vaulted-credential-shared";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

/** An Auth0 tenant's canonical domain, `<name>[.<region>].auth0.com`. It's the
 * connector's `tenant` schema (p0-connector-auth0), and the connector refuses
 * any other domain: the Management API's audience is always the canonical
 * domain, so P0 can't use a custom domain. */
export const AUTH0_TENANT_PATTERN =
  /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?(?:\.[a-z]{2})?\.auth0\.com$/;

export const INVALID_TENANT_DOMAIN =
  "Enter the tenant's canonical Auth0 domain, such as acme.us.auth0.com, in lowercase. A custom domain can't be used: the Auth0 Management API only answers on the canonical one.";

/** An Auth0 client id: letters, digits, `_` and `-`. It's the connector's
 * `clientId` schema (p0-connector-auth0). */
export const AUTH0_CLIENT_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

export const INVALID_CLIENT_ID =
  "Enter the client ID of the Auth0 machine-to-machine application, as the application's settings show it.";

// A connector's id names its Terraform resources, and each tenant picks its
// connector by it. The pattern and its 63-character limit are a DNS label's
// (RFC 1123), as Kubernetes names an object.
const CONNECTOR_ID_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
const CONNECTOR_ID_MAX_LENGTH = 63;

export const INVALID_CONNECTOR_ID = `Use lowercase letters, digits and hyphens only, starting and ending with a letter or digit, up to ${CONNECTOR_ID_MAX_LENGTH} characters.`;

/** Where a tenant's client secret is vaulted: the customer's own AWS Secrets
 * Manager or Google Secret Manager, in the same cloud as the tenant's
 * connector. P0 stores where the secret is, never the secret. */
export const Auth0SecretVault = withoutAzureKeyVault(
  newVaultElement({ owner: "tenant" })
);

/** Each artifact the install produces belongs to one of two items. The
 * deployed connector, its identity and P0's permission to invoke it belong to
 * `connector`. A tenant's client secret, and the connector's read on it,
 * belong to `tenant`. */
export const Auth0Components = InstallSpec({
  // One item per deployed connector, keyed by an id the customer chooses. A
  // connector serves every tenant that points at it.
  //
  // The connector's identity isn't stored: its IAM role, or its service
  // account, is named after the connector (see `toConnectorIdentity`), as a
  // connector the customer deploys themselves must name it too.
  connector: ItemComponent({
    description:
      "Runs P0's Auth0 connector in your AWS account or Google Cloud project. One connector can serve many tenants, so one is usually enough.",
    label: "Connector",
    maturity: "beta",
    schema: { hosting: ConnectorHosting },
    type: "string",
    validator: async (id) =>
      id.length <= CONNECTOR_ID_MAX_LENGTH && CONNECTOR_ID_PATTERN.test(id)
        ? undefined
        : INVALID_CONNECTOR_ID,
  }),
  // One item per Auth0 tenant, keyed by the tenant's canonical domain.
  //
  // The domain has no item validator here: the install framework's error for
  // one repeats the whole request, which may hold a secret pasted in place of
  // its secret's name. The backend checks it instead (`AUTH0_TENANT_PATTERN`).
  tenant: ItemComponent({
    description:
      "An Auth0 tenant P0 can grant roles, permissions and organization memberships in, through one of your connectors",
    label: "Tenant",
    maturity: "beta",
    // The connector, the vault, the client and its secret can't change once
    // the item exists: the tenant's Terraform grants exactly that connector
    // read on exactly that secret. To rotate the client secret, the customer
    // adds a new version to the same secret. The form asks for these fields in
    // this order.
    schema: {
      connector: Element({
        description:
          "The connector that acts on this tenant. P0 lists the Auth0 connectors you've installed.",
        label: "Connector",
        step: "new",
        type: "dynamic",
      }),
      vault: Auth0SecretVault,
      clientId: Element({
        description:
          "The client ID of the machine-to-machine application the connector acts as, authorized for the Auth0 Management API.",
        label: "Client ID",
        step: "new",
        type: "string",
      }),
      clientSecretName: Element({
        description:
          "The name, not the ARN or the full resource name, of the secret that holds, or will hold, the application's client secret. The tenant's Terraform lets the connector read exactly this secret.",
        label: "Client secret's secret",
        step: "new",
        type: "string",
      }),
      allowedDomains: Element({
        description:
          "The email domains the connector acts for, as its DOMAIN_ALLOW_PATTERN read when the tenant was last validated. To change them, change the connector's deployment.",
        label: "Allowed domains",
        type: "generated",
      }),
    },
    type: "string",
  }),
});
